import api from "@/api/axios";

export default {
  namespaced: true,
  state: () => ({
    items: [],
    allItems: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      totalElements: 0
    }
  }),
  getters: {
    getItems: (state) => state.items,
    getAllItems: (state) => state.allItems,
    getPagination: (state) => state.pagination,
  },
  mutations: {
    setItems(state, items) {
      state.items = items
    },
    setAllItems(state, items) {
      state.allItems = items
    },
    setPagination(state, paginationData) {
      state.pagination = paginationData;
    }
  },
  actions: {
    async fetchAll({ commit, dispatch }) {
      try {
        dispatch('ui/startLoading', null, { root: true });
        const response = await api.get('/sale/all');
        const salesNormalized = normalizedSale(response.data);
        commit('setAllItems', salesNormalized);
      } catch (error) {
        const msgError = 'Erro ao carregar o histórico de vendas';

        dispatch('ui/notify',  { 
          message: msgError, 
          color: 'error' 
        }, { root: true } );

        commit('setAllItems', []);
        console.error(`${msgError}: `, error);
      } finally {
        dispatch('ui/stopLoading', null, { root: true });
      }
    },
    async fetchAllOnPagination({ commit, dispatch }, params = {}) {
      try {
        dispatch('ui/startLoading', null, { root: true });

        const requestParams = {
          page: params.page || 0,
          size: params.size || 10,
          search: params.search || null,
        };

        if (params.startDate && params.endDate) {
          requestParams.startDate = params.startDate;
          requestParams.endDate = params.endDate;
        }

        const response = await api.get('/sale/pagination', { params: requestParams });

        const salesNormalized = normalizedSale(response.data.content);
        commit('setItems', salesNormalized);
        commit('setPagination', {
          currentPage: response.data.number,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements
        });
      } catch (error) {
        const msgError = 'Erro ao carregar o histórico de vendas';

        dispatch('ui/notify',  { 
          message: msgError, 
          color: 'error' 
        }, { root: true } );

        commit('setItems', []);
        commit('setPagination', { currentPage: 0, totalPages: 0, totalElements: 0 });
        
        console.error('Erro ao carregar o histórico de vendas', error);
      } finally {
        dispatch('ui/stopLoading', null, { root: true });
      }
    },
    async save({ commit, dispatch, rootGetters }, newItem) {
      try {
        dispatch('ui/startLoading', null, { root: true });

        const userLogged = rootGetters['auth/currentUser'];
        newItem.sellerName = userLogged ? userLogged.name : 'Desconhecido';

        await api.post('/sale/save', newItem);
        await dispatch('products/fetchAll', null, { root: true });

        dispatch('ui/notify', {
          message: 'Venda registrada com sucesso!',
          color: 'success'
        }, { root: true });

        return true;
      } catch (error) {
        dispatch('ui/notify',  { 
          message: 'Não foi possível registrar esta venda', 
          color: 'error' 
        }, { root: true } );
        console.error('Erro ao registrar a venda: ', error);

        return false;
      } finally {
        dispatch('ui/stopLoading', null, { root: true });
      }
    },
    async update({ commit, dispatch }, editedItem) {
      try {
        dispatch('ui/startLoading', null, { root: true });

        const saleStatusMap = {
          'Concluída': 'COMPLETED',
          'Pendente': 'PENDING',
          'Cancelada': 'CANCELED',
          'Reembolsada': 'REFUNDED'
        };
        editedItem.status = saleStatusMap[editedItem.status] || editedItem.status;

        await api.put(`/sale/update/${editedItem.id}`, editedItem);
        await dispatch('fetchAll');
        await dispatch('products/fetchAll', null, { root: true });

        dispatch('ui/notify', {
          message: 'Venda atualizada com sucesso!',
          color: 'success'
        }, { root: true });

        return true;
      } catch (error) {
        console.error('Erro ao editar a venda: ', error);
        
        dispatch('ui/notify', {
          message: 'Erro ao tentar atualizar esta Venda.',
          color: 'error'
        }, { root: true });

        return false;
      } finally {
        dispatch('ui/stopLoading', null, { root: true });
      }
    },
    // Para uma venda, o delete apenas CANCELA a venda, garantindo a auditoria dos dados.
    async delete({ state, dispatch }, id) {
      try {
        dispatch('ui/startLoading', null, { root: true });

        const existingSale = state.items.find(sale => sale.id === id);

        if (!existingSale) {
          throw new Error('Venda não encontrada na memória.');
        }

        const saleToCancel = JSON.parse(JSON.stringify(existingSale));
        saleToCancel.status = 'CANCELED';

        await dispatch('update', saleToCancel);

        return true;
      } catch (error) {
        dispatch('ui/notify',  { 
          message: 'Não foi possível cancelar esta venda.', 
          color: 'error'
        }, { root: true } );
        console.error('Erro ao cancelar a venda: ', error);

        return false;
      } finally {
        dispatch('ui/stopLoading', null, { root: true });
      }
    }
  }
}

/**
 * Normaliza os dados brutos vindos do Back-end.
 * Converte Enums para labels amigáveis e adiciona metadados.
 * @param {Array} data - Array de objetos vindos da API.
 * @returns {Array} Array de objetos normalizados.
 */
function normalizedSale(data) {
  if (!data || !Array.isArray(data)) return [];

  const saleStatusMap = {
    'COMPLETED': 'Concluída',
    'PENDING': 'Pendente',
    'CANCELED': 'Cancelada',
    'REFUNDED': 'Reembolsada'
  };

  return data.map(item => ({
    ...item,
    entity: 'sales',
    status: saleStatusMap[item.status] || item.status,
  }));
}
